import { jsx as _jsx, jsxs as _jsxs, Fragment as _Fragment } from "react/jsx-runtime";
import { Timestamp } from '@bufbuild/protobuf';
import { SpecraBadge, SpecraButton, SpecraFormField, SpecraInput, SpecraSelect } from '@mono/ui-components';
import { Testids } from '@mono/ui-selectors';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { useEffect, useRef, useState } from 'react';
import { useLocation } from 'react-router-dom';
import { useAuth } from '../../contexts/authContext';
import { useResolveAction } from '../../lib/access/useResolveAction';
import { rfiSectionClient } from './rfiSectionHooks';
const empty = ()=>({
        packageId: '',
        itemIds: [],
        versionId: '',
        supersedesId: '',
        submittedAt: '',
        submittedBy: '',
        channel: '',
        recipient: '',
        receiptReference: '',
        notes: '',
        requests: {}
    });
export function RfiSubmissionPanel({ rfiResponseProjectId, zouProjectId }) {
    const { user } = useAuth();
    const storageKey = `rfi-submission:${user?.id}:${rfiResponseProjectId}`;
    return /*#__PURE__*/ _jsx(SubmissionComposer, {
        rfiResponseProjectId: rfiResponseProjectId,
        zouProjectId: zouProjectId,
        storageKey: storageKey
    }, storageKey);
}
function SubmissionComposer({ rfiResponseProjectId, storageKey, zouProjectId }) {
    const cache = useQueryClient();
    const access = useResolveAction({
        kind: 'rfi_response',
        id: rfiResponseProjectId,
        objectType: 'delivery.programs',
        objectId: zouProjectId
    }, 'record_submission');
    const [draft, setDraft] = useState(()=>{
        try {
            return {
                ...empty(),
                ...JSON.parse(sessionStorage.getItem(storageKey) || '{}')
            };
        } catch  {
            return empty();
        }
    });
    const current = useRef(draft);
    const persist = (patch)=>{
        current.current = {
            ...current.current,
            ...patch
        };
        sessionStorage.setItem(storageKey, JSON.stringify(current.current));
        setDraft(current.current);
    };
    // Keys bind the complete payload, so editing retained values never reuses a key for different data.
    const requestId = (operation, payload)=>{
        const key = JSON.stringify([
            operation,
            payload
        ]);
        const existing = current.current.requests[key];
        if (existing) return existing;
        const id = crypto.randomUUID();
        persist({
            requests: {
                ...current.current.requests,
                [key]: id
            }
        });
        return id;
    };
    const [busy, setBusy] = useState('');
    const [error, setError] = useState('');
    const lock = useRef(false);
    const run = async (label, action, write = true)=>{
        if (lock.current || write && !access.allowed) return;
        lock.current = true;
        setBusy(label);
        setError('');
        try {
            await action();
        } catch  {
            setError('The action could not be completed. Your values are retained. Refresh to check current access and packages, then retry.');
        } finally{
            lock.current = false;
            setBusy('');
        }
    };
    const scope = {
        rfiResponseProjectId
    };
    const packages = useQuery({
        queryKey: [
            'rfi-packages',
            rfiResponseProjectId
        ],
        queryFn: async ()=>(await rfiSectionClient.listRfiPackages(scope)).packages
    });
    const history = useQuery({
        queryKey: [
            'rfi-submissions',
            rfiResponseProjectId
        ],
        queryFn: async ()=>(await rfiSectionClient.listRfiSubmissions(scope)).submissions
    });
    const version = useQuery({
        queryKey: [
            'rfi-submission-version',
            rfiResponseProjectId,
            draft.versionId
        ],
        enabled: !!draft.versionId,
        queryFn: async ()=>(await rfiSectionClient.getRfiPackageVersion({
                ...scope,
                versionId: draft.versionId
            })).version
    });
    const selected = packages.data?.find((p)=>p.id === draft.packageId);
    const submissions = history.isError ? undefined : history.data;
    const predecessor = submissions?.find((s)=>s.id === draft.supersedesId);
    const leaves = submissions?.filter((s)=>!submissions.some((other)=>other.supersedesId === s.id)) ?? [];
    const validSelection = !!selected && draft.itemIds.length > 0 && draft.itemIds.every((id)=>selected.items.some((i)=>i.id === id));
    const disabled = !!busy || !access.allowed;
    const frozen = !!draft.versionId;
    const refresh = async ()=>{
        void cache.invalidateQueries({
            queryKey: [
                'rfi-feedback-history',
                rfiResponseProjectId
            ]
        });
        await Promise.all([
            packages.refetch(),
            history.refetch(),
            ...draft.versionId ? [
                version.refetch()
            ] : []
        ]);
    };
    return /*#__PURE__*/ _jsxs("section", {
        className: "max-w-3xl space-y-3 rounded-md border border-border bg-card p-4",
        "data-testid": Testids.rfiSubmission.panel,
        "aria-busy": !!busy,
        children: [
            /*#__PURE__*/ _jsx("h2", {
                className: "text-lg font-medium",
                children: "Submission history"
            }),
            /*#__PURE__*/ _jsx("p", {
                children: "Record an actual submission that already happened. Recording sends nothing externally. Each submission retains an immutable package version; revisions never overwrite earlier submissions."
            }),
            /*#__PURE__*/ _jsx("output", {
                "aria-live": "polite",
                "data-testid": Testids.rfiSubmission.status,
                "data-loading": !!busy || packages.isFetching || history.isFetching || version.isFetching,
                children: busy || (packages.isFetching || history.isFetching || version.isFetching ? 'Loading submission history and packages…' : '')
            }),
            error && /*#__PURE__*/ _jsx("p", {
                role: "alert",
                "data-testid": Testids.rfiSubmission.error,
                children: error
            }),
            (packages.isError || history.isError) && /*#__PURE__*/ _jsx("p", {
                role: "alert",
                children: "History or packages unavailable. Refresh to retry; no current package will replace a historical version."
            }),
            /*#__PURE__*/ _jsx(SpecraButton, {
                "data-testid": Testids.rfiSubmission.refresh,
                disabled: !!busy,
                onClick: ()=>void run('Refreshing history…', refresh, false),
                children: "Refresh submission history and packages"
            }),
            !access.allowed && /*#__PURE__*/ _jsx("p", {
                children: access.isLoading ? 'Checking recording permission…' : 'Read-only history. Recording requires permission to update this response in Growth.'
            }),
            !history.isLoading && !history.isError && !history.data?.length && /*#__PURE__*/ _jsx("p", {
                children: "No actual submissions recorded."
            }),
            access.allowed && /*#__PURE__*/ _jsxs("fieldset", {
                disabled: disabled,
                className: "space-y-3",
                children: [
                    /*#__PURE__*/ _jsx("legend", {
                        children: "Record an actual submission"
                    }),
                    /*#__PURE__*/ _jsx(SpecraFormField, {
                        label: "Revises submission",
                        children: /*#__PURE__*/ _jsxs(SpecraSelect, {
                            "aria-label": "Revises submission",
                            "data-testid": Testids.rfiSubmission.predecessor,
                            value: draft.supersedesId,
                            disabled: frozen || history.isError,
                            onChange: (e)=>{
                                const prior = leaves.find((s)=>s.id === e.target.value);
                                persist({
                                    supersedesId: e.target.value,
                                    packageId: prior?.packageId ?? '',
                                    itemIds: []
                                });
                            },
                            children: [
                                /*#__PURE__*/ _jsx("option", {
                                    value: "",
                                    children: "First submission"
                                }),
                                leaves.map((s)=>/*#__PURE__*/ _jsxs("option", {
                                        value: s.id,
                                        children: [
                                            s.id,
                                            " · ",
                                            s.submittedBy,
                                            " · ",
                                            s.submittedAt?.toDate().toLocaleString()
                                        ]
                                    }, s.id))
                            ]
                        })
                    }),
                    /*#__PURE__*/ _jsx(SpecraFormField, {
                        label: "Package to capture",
                        children: /*#__PURE__*/ _jsxs(SpecraSelect, {
                            "aria-label": "Package to capture",
                            "data-testid": Testids.rfiSubmission.packageSelect,
                            value: draft.packageId,
                            disabled: frozen || !!draft.supersedesId || packages.isError,
                            onChange: (e)=>persist({
                                    packageId: e.target.value,
                                    itemIds: []
                                }),
                            children: [
                                /*#__PURE__*/ _jsx("option", {
                                    value: "",
                                    children: "Choose a package"
                                }),
                                packages.data?.map((p)=>/*#__PURE__*/ _jsx("option", {
                                        value: p.id,
                                        children: p.name
                                    }, p.id))
                            ]
                        })
                    }),
                    draft.packageId && !selected && !packages.isLoading && /*#__PURE__*/ _jsx("p", {
                        role: "alert",
                        children: "Selected package is missing or unavailable. Refresh or choose another package."
                    }),
                    !frozen && selected && /*#__PURE__*/ _jsxs(_Fragment, {
                        children: [
                            /*#__PURE__*/ _jsx("p", {
                                children: "Select exactly the items that were submitted. Unselected items are excluded."
                            }),
                            !selected.items.length && /*#__PURE__*/ _jsx("p", {
                                children: "This package has no final items. Add items in Proposed final packages, then refresh."
                            }),
                            selected.items.map((i)=>/*#__PURE__*/ _jsxs(SpecraButton, {
                                    variant: "secondary",
                                    "data-testid": Testids.rfiSubmission.selectItem,
                                    "data-item-id": i.id,
                                    "aria-pressed": draft.itemIds.includes(i.id),
                                    onClick: ()=>persist({
                                            itemIds: draft.itemIds.includes(i.id) ? draft.itemIds.filter((id)=>id !== i.id) : [
                                                ...draft.itemIds,
                                                i.id
                                            ]
                                        }),
                                    children: [
                                        draft.itemIds.includes(i.id) ? 'Selected: ' : 'Select: ',
                                        i.filename || i.kind,
                                        " ·",
                                        ' ',
                                        i.documentVersionId || `${i.artifactId} v${i.artifactVersion}`
                                    ]
                                }, i.id))
                        ]
                    }),
                    !frozen && draft.itemIds.length > 0 && !validSelection && /*#__PURE__*/ _jsx("p", {
                        role: "alert",
                        children: "Selected items changed or are unavailable. Choose the exact items again."
                    }),
                    /*#__PURE__*/ _jsx(SpecraButton, {
                        "data-testid": Testids.rfiSubmission.capture,
                        disabled: disabled || frozen || !validSelection || packages.isError || history.isError || !!draft.supersedesId && !predecessor,
                        onClick: ()=>void run('Capturing immutable package version…', async ()=>{
                                const payload = {
                                    ...scope,
                                    packageId: draft.packageId,
                                    itemIds: [
                                        ...draft.itemIds
                                    ].sort()
                                };
                                const result = await rfiSectionClient.captureRfiPackageVersion({
                                    ...payload,
                                    requestId: requestId('capture', payload)
                                });
                                if (!result.version) throw new Error('Missing capture');
                                persist({
                                    versionId: result.version.id
                                });
                            }),
                        children: "Capture selected immutable version"
                    }),
                    frozen && /*#__PURE__*/ _jsxs(_Fragment, {
                        children: [
                            /*#__PURE__*/ _jsxs("p", {
                                children: [
                                    "Captured package version ",
                                    draft.versionId,
                                    ". These items are fixed."
                                ]
                            }),
                            version.isError && /*#__PURE__*/ _jsx("p", {
                                role: "alert",
                                children: "Captured version unavailable. Refresh to retry; recording is disabled."
                            }),
                            !version.isError && version.data && /*#__PURE__*/ _jsx(VersionItems, {
                                version: version.data,
                                download: (itemId)=>run('Preparing exact download…', async ()=>{
                                        await downloadItem(rfiResponseProjectId, draft.packageId, itemId);
                                    }, false),
                                disabled: !!busy
                            })
                        ]
                    }),
                    [
                        'submittedAt',
                        'submittedBy',
                        'channel',
                        'recipient',
                        'receiptReference',
                        'notes'
                    ].map((field)=>/*#__PURE__*/ _jsx(SpecraFormField, {
                            label: fieldLabels[field],
                            children: /*#__PURE__*/ _jsx(SpecraInput, {
                                "aria-label": fieldLabels[field],
                                type: field === 'submittedAt' ? 'datetime-local' : 'text',
                                "data-testid": Testids.rfiSubmission[field],
                                value: draft[field],
                                maxLength: field === 'notes' ? 10000 : 512,
                                onChange: (e)=>persist({
                                        [field]: e.target.value
                                    })
                            })
                        }, field)),
                    /*#__PURE__*/ _jsx(SpecraButton, {
                        "data-testid": Testids.rfiSubmission.record,
                        disabled: disabled || !version.data || version.isError || history.isError || !draft.submittedAt || !Number.isFinite(new Date(draft.submittedAt).getTime()) || !draft.submittedBy.trim() || !draft.channel.trim() || !draft.recipient.trim(),
                        onClick: ()=>void run('Recording actual submission…', async ()=>{
                                const payload = {
                                    packageId: draft.packageId,
                                    packageVersionId: draft.versionId,
                                    supersedesId: draft.supersedesId,
                                    submittedAt: Timestamp.fromDate(new Date(draft.submittedAt)),
                                    submittedBy: draft.submittedBy.trim(),
                                    channel: draft.channel.trim(),
                                    recipient: draft.recipient.trim(),
                                    receiptReference: draft.receiptReference.trim(),
                                    notes: draft.notes.trim()
                                };
                                await rfiSectionClient.recordRfiSubmission({
                                    ...scope,
                                    submission: {
                                        ...payload,
                                        requestId: requestId('record', payload)
                                    }
                                });
                                persist(empty());
                                await history.refetch();
                            }),
                        children: "Record actual submission"
                    }),
                    /*#__PURE__*/ _jsx(SpecraButton, {
                        variant: "secondary",
                        disabled: disabled,
                        onClick: ()=>persist({
                                versionId: '',
                                itemIds: [],
                                requests: {}
                            }),
                        children: "Start a new capture"
                    })
                ]
            }),
            submissions?.map((submission)=>/*#__PURE__*/ _jsx(SubmissionEntry, {
                    submission: submission,
                    rfiResponseProjectId: rfiResponseProjectId,
                    canWrite: access.allowed
                }, submission.id))
        ]
    });
}
const fieldLabels = {
    submittedAt: 'Actual submission time',
    submittedBy: 'Actual submitted by',
    channel: 'Submission channel',
    recipient: 'Recipient',
    receiptReference: 'Original receipt reference (optional)',
    notes: 'Submission notes (optional)'
};
async function downloadItem(rfiResponseProjectId, packageId, itemId) {
    const result = await rfiSectionClient.downloadRfiPackageArtifact({
        rfiResponseProjectId,
        packageId,
        itemId
    });
    if (!result.url) throw new Error('Download unavailable');
    const link = document.createElement('a');
    link.href = result.url;
    link.download = result.filename;
    link.click();
}
function VersionItems({ version, download, disabled }) {
    return /*#__PURE__*/ _jsxs("div", {
        children: [
            /*#__PURE__*/ _jsxs("p", {
                className: "break-all",
                children: [
                    version.name,
                    " · Package ",
                    version.packageId,
                    " · Immutable version ",
                    version.id,
                    " · Captured by",
                    ' ',
                    version.createdBy,
                    " at ",
                    version.createdAt?.toDate().toLocaleString()
                ]
            }),
            /*#__PURE__*/ _jsx("ul", {
                children: version.items.map((i)=>/*#__PURE__*/ _jsxs("li", {
                        className: "break-all rounded-md border border-border p-3",
                        "data-testid": Testids.rfiSubmission.versionItem,
                        "data-item-id": i.id,
                        "data-version-id": version.id,
                        "data-artifact-id": i.artifactId,
                        "data-artifact-version": i.artifactVersion,
                        "data-document-version-id": i.documentVersionId,
                        children: [
                            /*#__PURE__*/ _jsxs("p", {
                                children: [
                                    i.filename || i.kind,
                                    " · ",
                                    i.kind,
                                    " · Item ",
                                    i.id
                                ]
                            }),
                            /*#__PURE__*/ _jsxs("p", {
                                children: [
                                    "Document ",
                                    i.documentId || '—',
                                    " · version ",
                                    i.documentVersionId || '—',
                                    " · Artifact",
                                    ' ',
                                    i.artifactId || '—',
                                    " · version ",
                                    i.artifactVersion,
                                    " · SHA-256 ",
                                    i.contentSha256
                                ]
                            }),
                            /*#__PURE__*/ _jsxs("p", {
                                children: [
                                    "Not validated",
                                    i.validationStatus === 'unsupported' ? ' — external file validation is unsupported' : ' — no compliance approval implied'
                                ]
                            }),
                            i.artifactId && /*#__PURE__*/ _jsx(SpecraButton, {
                                "data-testid": Testids.rfiSubmission.download,
                                disabled: disabled,
                                onClick: ()=>void download(i.id),
                                children: "Download exact submitted file"
                            })
                        ]
                    }, i.id))
            })
        ]
    });
}
function SubmissionEntry({ submission: s, rfiResponseProjectId, canWrite }) {
    const { user } = useAuth();
    const key = `rfi-receipt:${user?.id}:${rfiResponseProjectId}:${s.id}`;
    const [draft, setDraft] = useState(()=>{
        try {
            return JSON.parse(sessionStorage.getItem(key) || 'null') || {
                reference: '',
                notes: '',
                requests: {}
            };
        } catch  {
            return {
                reference: '',
                notes: '',
                requests: {}
            };
        }
    });
    const update = (next)=>{
        sessionStorage.setItem(key, JSON.stringify(next));
        setDraft(next);
    };
    const [busy, setBusy] = useState(false);
    const lock = useRef(false);
    const [error, setError] = useState('');
    const scope = {
        rfiResponseProjectId
    };
    const version = useQuery({
        queryKey: [
            'rfi-submission-version',
            rfiResponseProjectId,
            s.packageVersionId
        ],
        queryFn: async ()=>(await rfiSectionClient.getRfiPackageVersion({
                ...scope,
                versionId: s.packageVersionId
            })).version
    });
    const receipts = useQuery({
        queryKey: [
            'rfi-submission-receipts',
            rfiResponseProjectId,
            s.id
        ],
        queryFn: async ()=>(await rfiSectionClient.listRfiSubmissionReceipts({
                ...scope,
                submissionId: s.id
            })).receipts
    });
    const run = async (action)=>{
        if (lock.current) return;
        lock.current = true;
        setBusy(true);
        setError('');
        try {
            await action();
        } catch  {
            setError('Unable to complete this action. Values retained; refresh and retry.');
        } finally{
            lock.current = false;
            setBusy(false);
        }
    };
    const unavailable = version.isError || receipts.isError;
    const location = useLocation();
    useEffect(()=>{
        const params = new URLSearchParams(location.search);
        if (params.get('submission') !== s.id || !version.data || unavailable) return;
        if (params.get('packageVersion') && params.get('packageVersion') !== s.packageVersionId) return;
        const row = document.getElementById(`rfi-submission-${s.id}`);
        row?.focus({
            preventScroll: true
        });
        row?.scrollIntoView({
            block: 'center'
        });
    }, [
        location.search,
        s.id,
        s.packageVersionId,
        version.data,
        unavailable
    ]);
    const confirmed = !!s.receiptReference || !!receipts.data?.length;
    return /*#__PURE__*/ _jsxs("article", {
        className: "space-y-3 rounded-md border border-border p-3",
        "data-testid": Testids.rfiSubmission.entry,
        id: `rfi-submission-${s.id}`,
        tabIndex: -1,
        "data-submission-id": s.id,
        "data-version-id": s.packageVersionId,
        "data-supersedes-id": s.supersedesId,
        "aria-busy": busy || version.isFetching || receipts.isFetching,
        children: [
            !unavailable && /*#__PURE__*/ _jsxs(_Fragment, {
                children: [
                    /*#__PURE__*/ _jsxs("h3", {
                        children: [
                            "Submission ",
                            s.id
                        ]
                    }),
                    /*#__PURE__*/ _jsx(SpecraBadge, {
                        variant: confirmed ? 'success' : 'warning',
                        children: receipts.isLoading ? 'Loading receipts…' : receipts.isError ? 'Receipt status unavailable' : confirmed ? 'Receipt recorded' : 'Unconfirmed'
                    }),
                    /*#__PURE__*/ _jsxs("p", {
                        children: [
                            "Actually submitted ",
                            s.submittedAt?.toDate().toLocaleString(),
                            " by ",
                            s.submittedBy,
                            " via",
                            ' ',
                            s.channel,
                            " to ",
                            s.recipient
                        ]
                    }),
                    /*#__PURE__*/ _jsxs("p", {
                        children: [
                            "Recorded by ",
                            s.recordedBy,
                            " at ",
                            s.recordedAt?.toDate().toLocaleString()
                        ]
                    }),
                    /*#__PURE__*/ _jsxs("p", {
                        children: [
                            s.supersedesId ? `Revises submission ${s.supersedesId}` : 'Original submission',
                            " · Immutable version ",
                            s.packageVersionId
                        ]
                    }),
                    s.notes && /*#__PURE__*/ _jsx("p", {
                        children: s.notes
                    }),
                    s.receiptReference && /*#__PURE__*/ _jsxs("p", {
                        children: [
                            "Original receipt: ",
                            s.receiptReference
                        ]
                    })
                ]
            }),
            (busy || version.isFetching || receipts.isFetching) && /*#__PURE__*/ _jsx("output", {
                "aria-live": "polite",
                children: "Loading exact submission details…"
            }),
            (error || version.isError || receipts.isError) && /*#__PURE__*/ _jsx("p", {
                role: "alert",
                children: error || 'Exact history unavailable. Refresh exact history to retry; current files will not be substituted.'
            }),
            /*#__PURE__*/ _jsx(SpecraButton, {
                variant: "secondary",
                disabled: busy,
                onClick: ()=>void run(async ()=>{
                        await Promise.all([
                            version.refetch(),
                            receipts.refetch()
                        ]);
                    }),
                children: "Refresh exact history"
            }),
            !unavailable && version.data && /*#__PURE__*/ _jsx(VersionItems, {
                version: version.data,
                disabled: busy,
                download: (id)=>run(()=>downloadItem(rfiResponseProjectId, s.packageId, id))
            }),
            /*#__PURE__*/ _jsx("ul", {
                children: !unavailable && receipts.data?.map((r)=>/*#__PURE__*/ _jsxs("li", {
                        children: [
                            "Receipt: ",
                            r.reference,
                            " · ",
                            r.notes,
                            " · Recorded by ",
                            r.recordedBy,
                            " at",
                            ' ',
                            r.recordedAt?.toDate().toLocaleString()
                        ]
                    }, r.id))
            }),
            canWrite && !unavailable && /*#__PURE__*/ _jsxs("fieldset", {
                disabled: busy,
                className: "space-y-2",
                children: [
                    /*#__PURE__*/ _jsx("legend", {
                        children: "Append a receipt; original submission and pins stay unchanged"
                    }),
                    /*#__PURE__*/ _jsx(SpecraFormField, {
                        label: "Receipt reference",
                        children: /*#__PURE__*/ _jsx(SpecraInput, {
                            "aria-label": "Receipt reference",
                            "data-testid": Testids.rfiSubmission.appendReference,
                            value: draft.reference,
                            onChange: (e)=>update({
                                    ...draft,
                                    reference: e.target.value
                                })
                        })
                    }),
                    /*#__PURE__*/ _jsx(SpecraFormField, {
                        label: "Receipt notes (optional)",
                        children: /*#__PURE__*/ _jsx(SpecraInput, {
                            "aria-label": "Receipt notes",
                            "data-testid": Testids.rfiSubmission.appendNotes,
                            value: draft.notes,
                            onChange: (e)=>update({
                                    ...draft,
                                    notes: e.target.value
                                })
                        })
                    }),
                    /*#__PURE__*/ _jsx(SpecraButton, {
                        "data-testid": Testids.rfiSubmission.appendReceipt,
                        disabled: busy || !draft.reference.trim(),
                        onClick: ()=>void run(async ()=>{
                                if (!canWrite) return;
                                const payload = {
                                    submissionId: s.id,
                                    reference: draft.reference.trim(),
                                    notes: draft.notes.trim()
                                };
                                const fingerprint = JSON.stringify(payload);
                                const requestId = draft.requests[fingerprint] || crypto.randomUUID();
                                update({
                                    ...draft,
                                    requests: {
                                        ...draft.requests,
                                        [fingerprint]: requestId
                                    }
                                });
                                await rfiSectionClient.appendRfiSubmissionReceipt({
                                    ...scope,
                                    packageId: s.packageId,
                                    receipt: {
                                        ...payload,
                                        requestId
                                    }
                                });
                                update({
                                    reference: '',
                                    notes: '',
                                    requests: {}
                                });
                                await receipts.refetch();
                            }),
                        children: "Append receipt"
                    })
                ]
            })
        ]
    });
}
